/**
 * Command Center — vertical configuration registry (INERT as of Slice 1).
 * =========================================================================
 * Extension point for the shared Command Center shell. Each vertical declares
 * its departments, action buttons and shell flags here so the shell never
 * needs `if (vertical === 'automotive')` conditionals (wired in Slice 3).
 *
 * SLICE 1: this module is intentionally NOT imported by CommandCenter.jsx yet.
 * It defines the existing property/hotel/restaurant behavior verbatim (to be
 * proven byte-equal by the Slice-3 snapshot test) plus the new automotive
 * config, so later slices can switch the shell over with zero behavior change.
 *
 * Adding a future vertical = add one entry here (+ optional panels in
 * VerticalPanels.jsx). The shared shell code is never edited again.
 */

// Property-management department tiles (mirrors CommandCenter.jsx CATEGORY_DEPTS
// + security, unchanged). `owns(r)` maps a tile -> the request rows it owns.
const PROPERTY_DEPARTMENTS = [
    { id: 'maintenance', i18nKey: 'commandCenter.act.dispatch.cat.maintenance' },
    { id: 'security', i18nKey: 'commandCenter.act.dispatch.cat.security' },
    { id: 'parking', i18nKey: 'commandCenter.act.dispatch.cat.parking' },
    { id: 'waste_recycling', i18nKey: 'commandCenter.act.dispatch.cat.waste_recycling' },
    { id: 'amenities', i18nKey: 'commandCenter.act.dispatch.cat.amenities' },
    { id: 'building', i18nKey: 'commandCenter.act.dispatch.cat.building' },
];

const HOTEL_DEPARTMENTS = [
    { id: 'housekeeping', i18nKey: 'commandCenter.act.dispatch.cat.housekeeping' },
    { id: 'maintenance', i18nKey: 'commandCenter.act.dispatch.cat.maintenance' },
    { id: 'concierge', i18nKey: 'commandCenter.act.dispatch.cat.concierge' },
    { id: 'security', i18nKey: 'commandCenter.act.dispatch.cat.security' },
    { id: 'amenities', i18nKey: 'commandCenter.act.dispatch.cat.amenities' },
];

// Automotive departments — configuration of the SAME framework (Slice 3+).
const AUTOMOTIVE_DEPARTMENTS = [
    { id: 'sales', i18nKey: 'commandCenter.automotive.dept.sales' },
    { id: 'vehicle_inventory', i18nKey: 'commandCenter.automotive.dept.inventory' },
    { id: 'customer_inquiries', i18nKey: 'commandCenter.automotive.dept.inquiries' },
    { id: 'test_drives', i18nKey: 'commandCenter.automotive.dept.testDrives' },
    { id: 'trade_ins', i18nKey: 'commandCenter.automotive.dept.tradeIns' },
    { id: 'offers', i18nKey: 'commandCenter.automotive.dept.offers' },
    { id: 'appointments', i18nKey: 'commandCenter.automotive.dept.appointments' },
    { id: 'deliveries', i18nKey: 'commandCenter.automotive.dept.deliveries' },
    // F&I / Insurance — ONE tile, not two. Insurance is already one of the four
    // products of the same buyer-facing "Finance & Insurance" flow (financing,
    // warranty, insurance, trade-in), so the manager view mirrors the buyer's
    // single journey. The tile opens the EXISTING CCFILeadsPanel; the
    // confidentiality-scoped insurance quote threads keep their own scoping.
    { id: 'finance_insurance', i18nKey: 'commandCenter.automotive.dept.financeInsurance' },
];

// Restaurant departments — declared here so Phase 2 can render the tile strip
// from config alone. NOT rendered yet (`showOps: false`), so restaurant
// behaviour is unchanged by this entry. Restaurant wording only.
const RESTAURANT_DEPARTMENTS = [
    { id: 'reviews', i18nKey: 'commandCenter.restaurant.dept.reviews' },
    { id: 'recovery', i18nKey: 'commandCenter.restaurant.dept.recovery' },
    { id: 'vouchers', i18nKey: 'commandCenter.restaurant.dept.vouchers' },
    { id: 'tap_cards', i18nKey: 'commandCenter.restaurant.dept.tapCards' },
    { id: 'card_fleet', i18nKey: 'commandCenter.restaurant.dept.cardFleet' },
];

// Action-button ids (labels resolved via i18n in the shell; handlers wired in
// Slice 4/5). Existing verticals keep their current buttons; listed here only
// for completeness and are NOT consumed until the shell is switched over.
export const VERTICAL_CONFIG = {
    property_management: {
        showOps: true,
        overviewMode: 'ops',
        actionMode: 'ops',
        departments: PROPERTY_DEPARTMENTS,
        actions: ['dispatch', 'communication', 'announcement', 'assign', 'cameras'],
        trendKey: 'requests',
    },
    condo: {
        showOps: true,
        overviewMode: 'ops',
        actionMode: 'ops',
        departments: PROPERTY_DEPARTMENTS,
        actions: ['dispatch', 'communication', 'announcement', 'assign', 'cameras'],
        trendKey: 'requests',
    },
    hotel: {
        showOps: true,
        overviewMode: 'ops',
        actionMode: 'ops',
        departments: HOTEL_DEPARTMENTS,
        actions: ['dispatch', 'communication', 'announcement', 'assign', 'cameras'],
        trendKey: 'requests',
    },
    automotive: {
        showOps: true,
        overviewMode: 'automotive',
        actionMode: 'automotive',
        departments: AUTOMOTIVE_DEPARTMENTS,
        actions: ['manageVehicles', 'viewLeads', 'contactCustomer', 'communication',
                  'assignSalesperson', 'manageTestDrives', 'manageOffers'],
        trendKey: 'requests',
    },
    // Restaurant: reviews-centric, no department strip yet. Same flags as the
    // `default` entry below, so resolving here changes nothing visually; the
    // departments list is config for Phase 2.
    restaurant: {
        showOps: false,
        overviewMode: 'reviews',
        actionMode: 'none',
        departments: RESTAURANT_DEPARTMENTS,
        actions: [],
        trendKey: 'reviews',
        // Shared-shell strings that would otherwise read "properties". Any
        // vertical can override the same way; a vertical with no `wording`
        // keeps the shell's existing keys untouched.
        wording: {
            subtitle: 'commandCenter.restaurant.subtitle',
            scopeAll: 'commandCenter.restaurant.scopeAll',
            scopeOne: 'commandCenter.restaurant.scopeOne',
            selectScope: 'commandCenter.restaurant.selectLocations',
            locationsStat: 'commandCenter.restaurant.locationsStat',
        },
    },
    // Phase 0 — grocery registered, deliberately NEUTRAL (identical to
    // `default`): no department strip, no vertical actions, no restaurant
    // wording. Declared explicitly so a grocer can never silently inherit the
    // restaurant configuration. Grocery's own panels/wording land in Phase 1.
    grocery: {
        showOps: false,
        overviewMode: 'reviews',
        actionMode: 'none',
        departments: [],
        actions: [],
        trendKey: 'reviews',
    },
    // Medical: reviews-centric like restaurant, but every shared-shell string
    // that would otherwise read "locations"/"restaurants" is replaced with
    // clinic wording. No department strip, no vertical actions.
    medical: {
        showOps: false,
        overviewMode: 'reviews',
        actionMode: 'none',
        departments: [],
        actions: [],
        trendKey: 'reviews',
        wording: {
            subtitle: 'commandCenter.medical.subtitle',
            scopeAll: 'commandCenter.medical.scopeAll',
            scopeOne: 'commandCenter.medical.scopeOne',
            selectScope: 'commandCenter.medical.selectClinics',
            locationsStat: 'commandCenter.medical.clinicsStat',
        },
    },
    // Restaurant / default: no department strip (reviews-centric), unchanged.
    default: {
        showOps: false,
        overviewMode: 'reviews',
        actionMode: 'none',
        departments: [],
        actions: [],
        trendKey: 'reviews',
    },
};

export function getVerticalConfig(vertical) {
    return VERTICAL_CONFIG[vertical] || VERTICAL_CONFIG.default;
}

// ─────────────────────────────────────────────────────────────────────────────
// FRIENDLY-UX CONFIG (Command Center friendly pass).
// The shared shell + shared components hold ZERO vertical wording: each
// vertical supplies its own headline numbers, tour steps, tip and status
// sentence here. A vertical with no entry gets NO tour and NO tip — never
// another vertical's words, never generic filler.
// Adding a vertical later = ONE entry below (+ its i18n keys). Nothing else.
// ─────────────────────────────────────────────────────────────────────────────
const AUTOMOTIVE_FRIENDLY = {
    tourId: 'cc.automotive.v1',
    statusKey: 'commandCenter.friendly.automotive.status',
    statusField: 'open_inquiries',
    headlines: [
        { id: 'vehicles', field: 'vehicles_total', labelKey: 'commandCenter.automotive.vehiclesTotal', helpKey: 'commandCenter.friendly.automotive.help.vehicles', sub: { labelKey: 'commandCenter.automotive.vehiclesActive', field: 'vehicles_active' } },
        { id: 'questions', field: 'open_inquiries', labelKey: 'commandCenter.automotive.questions', helpKey: 'commandCenter.friendly.automotive.help.questions' },
        { id: 'testdrives', field: 'test_drives_pending', labelKey: 'commandCenter.automotive.testDrivesPending', helpKey: 'commandCenter.friendly.automotive.help.testDrives', sub: { labelKey: 'commandCenter.automotive.testDrivesBooked', field: 'test_drives_booked' } },
    ],
    tip: { titleKey: 'commandCenter.friendly.tip.title', bodyKey: 'commandCenter.friendly.automotive.tip' },
    actionsCard: { titleKey: 'commandCenter.friendly.automotive.actions.title' },
    itemLabelKey: 'commandCenter.friendly.automotive.itemLabel',
    emptyKey: 'commandCenter.friendly.automotive.empty',
    tourSteps: [
        { anchor: 'cc-headline-numbers', titleKey: 'commandCenter.friendly.automotive.tour.s1.title', bodyKey: 'commandCenter.friendly.automotive.tour.s1.body' },
        { anchor: 'cc-band-attention', titleKey: 'commandCenter.friendly.automotive.tour.s2.title', bodyKey: 'commandCenter.friendly.automotive.tour.s2.body' },
        { anchor: 'cc-friendly-actions', titleKey: 'commandCenter.friendly.automotive.tour.s3.title', bodyKey: 'commandCenter.friendly.automotive.tour.s3.body' },
        { anchor: 'cc-band-requests-header', titleKey: 'commandCenter.friendly.automotive.tour.s4.title', bodyKey: 'commandCenter.friendly.automotive.tour.s4.body' },
    ],
};

const PROPERTY_FRIENDLY = {
    tourId: 'cc.property.v1',
    statusKey: 'commandCenter.friendly.property.status',
    statusField: 'open_requests',
    headlines: [
        { id: 'open', field: 'open_requests', labelKey: 'commandCenter.ops.openRequests', helpKey: 'commandCenter.friendly.property.help.open', subDeltaField: 'requests_delta_pct' },
        { id: 'urgent', field: 'urgent_open', labelKey: 'commandCenter.ops.urgent', helpKey: 'commandCenter.friendly.property.help.urgent', sub: { labelKey: 'commandCenter.ops.resolved7d', field: 'resolved_7d' } },
        { id: 'response', field: 'avg_response_min', labelKey: 'commandCenter.ops.avgResponse', helpKey: 'commandCenter.friendly.property.help.response', unitKey: 'commandCenter.ops.minutes' },
    ],
    tip: { titleKey: 'commandCenter.friendly.tip.title', bodyKey: 'commandCenter.friendly.property.tip' },
    actionsCard: { titleKey: 'commandCenter.friendly.property.actions.title' },
    itemLabelKey: 'commandCenter.friendly.property.itemLabel',
    emptyKey: 'commandCenter.friendly.property.empty',
    tourSteps: [
        { anchor: 'cc-headline-numbers', titleKey: 'commandCenter.friendly.property.tour.s1.title', bodyKey: 'commandCenter.friendly.property.tour.s1.body' },
        { anchor: 'cc-band-attention', titleKey: 'commandCenter.friendly.property.tour.s2.title', bodyKey: 'commandCenter.friendly.property.tour.s2.body' },
        { anchor: 'cc-friendly-actions', titleKey: 'commandCenter.friendly.property.tour.s3.title', bodyKey: 'commandCenter.friendly.property.tour.s3.body' },
        { anchor: 'cc-band-requests-header', titleKey: 'commandCenter.friendly.property.tour.s4.title', bodyKey: 'commandCenter.friendly.property.tour.s4.body' },
    ],
};

const HOTEL_FRIENDLY = {
    tourId: 'cc.hotel.v1',
    statusKey: 'commandCenter.friendly.hotel.status',
    statusField: 'open_requests',
    headlines: [
        { id: 'open', field: 'open_requests', labelKey: 'commandCenter.ops.openRequests', helpKey: 'commandCenter.friendly.hotel.help.open', subDeltaField: 'requests_delta_pct' },
        { id: 'urgent', field: 'urgent_open', labelKey: 'commandCenter.ops.urgent', helpKey: 'commandCenter.friendly.hotel.help.urgent', sub: { labelKey: 'commandCenter.ops.resolved7d', field: 'resolved_7d' } },
        { id: 'response', field: 'avg_response_min', labelKey: 'commandCenter.ops.avgResponse', helpKey: 'commandCenter.friendly.hotel.help.response', unitKey: 'commandCenter.ops.minutes' },
    ],
    tip: { titleKey: 'commandCenter.friendly.tip.title', bodyKey: 'commandCenter.friendly.hotel.tip' },
    actionsCard: { titleKey: 'commandCenter.friendly.hotel.actions.title' },
    itemLabelKey: 'commandCenter.friendly.hotel.itemLabel',
    emptyKey: 'commandCenter.friendly.hotel.empty',
    tourSteps: [
        { anchor: 'cc-headline-numbers', titleKey: 'commandCenter.friendly.hotel.tour.s1.title', bodyKey: 'commandCenter.friendly.hotel.tour.s1.body' },
        { anchor: 'cc-band-attention', titleKey: 'commandCenter.friendly.hotel.tour.s2.title', bodyKey: 'commandCenter.friendly.hotel.tour.s2.body' },
        { anchor: 'cc-friendly-actions', titleKey: 'commandCenter.friendly.hotel.tour.s3.title', bodyKey: 'commandCenter.friendly.hotel.tour.s3.body' },
        { anchor: 'cc-band-requests-header', titleKey: 'commandCenter.friendly.hotel.tour.s4.title', bodyKey: 'commandCenter.friendly.hotel.tour.s4.body' },
    ],
};

// Restaurant: the same narrative as every other vertical, fed only by fields the
// restaurant overview already returns (total_reviews, avg_rating, rating_sample,
// avg_daily_velocity, active_locations, reviews_delta_pct). No invented metric.
const RESTAURANT_FRIENDLY = {
    tourId: 'cc.restaurant.v1',
    statusKey: 'commandCenter.friendly.restaurant.status',
    statusField: 'total_reviews',
    headlines: [
        { id: 'reviews', field: 'total_reviews', labelKey: 'commandCenter.restaurant.newReviews', helpKey: 'commandCenter.friendly.restaurant.help.reviews', subDeltaField: 'reviews_delta_pct' },
        { id: 'rating', field: 'avg_rating', labelKey: 'commandCenter.restaurant.avgRating', helpKey: 'commandCenter.friendly.restaurant.help.rating', sub: { labelKey: 'commandCenter.restaurant.ratingsCounted', field: 'rating_sample' } },
        { id: 'taps', field: 'avg_daily_velocity', labelKey: 'commandCenter.restaurant.tapsPerDay', helpKey: 'commandCenter.friendly.restaurant.help.taps', sub: { labelKey: 'commandCenter.restaurant.locationsLive', field: 'active_locations' } },
    ],
    tip: { titleKey: 'commandCenter.friendly.tip.title', bodyKey: 'commandCenter.friendly.restaurant.tip' },
    itemLabelKey: 'commandCenter.friendly.restaurant.itemLabel',
    emptyKey: 'commandCenter.friendly.restaurant.empty',
    // Anchors that exist in reviews mode only — no step points at an ops-only
    // element, so no step can ever land on nothing.
    tourSteps: [
        { anchor: 'cc-headline-numbers', titleKey: 'commandCenter.friendly.restaurant.tour.s1.title', bodyKey: 'commandCenter.friendly.restaurant.tour.s1.body' },
        { anchor: 'cc-band-attention', titleKey: 'commandCenter.friendly.restaurant.tour.s2.title', bodyKey: 'commandCenter.friendly.restaurant.tour.s2.body' },
        { anchor: 'cc-band-overview', titleKey: 'commandCenter.friendly.restaurant.tour.s3.title', bodyKey: 'commandCenter.friendly.restaurant.tour.s3.body' },
        { anchor: 'cc-band-whatchanged', titleKey: 'commandCenter.friendly.restaurant.tour.s4.title', bodyKey: 'commandCenter.friendly.restaurant.tour.s4.body' },
    ],
};

// Medical: the same narrative, fed ONLY by fields the reviews overview already
// returns. Patient-first wording; nothing invented.
const MEDICAL_FRIENDLY = {
    tourId: 'cc.medical.v1',
    statusKey: 'commandCenter.friendly.medical.status',
    statusField: 'total_reviews',
    headlines: [
        { id: 'reviews', field: 'total_reviews', labelKey: 'commandCenter.medical.newReviews', helpKey: 'commandCenter.friendly.medical.help.reviews', subDeltaField: 'reviews_delta_pct' },
        { id: 'rating', field: 'avg_rating', labelKey: 'commandCenter.medical.avgRating', helpKey: 'commandCenter.friendly.medical.help.rating', sub: { labelKey: 'commandCenter.medical.ratingsCounted', field: 'rating_sample' } },
        { id: 'taps', field: 'avg_daily_velocity', labelKey: 'commandCenter.medical.tapsPerDay', helpKey: 'commandCenter.friendly.medical.help.taps', sub: { labelKey: 'commandCenter.medical.clinicsLive', field: 'active_locations' } },
    ],
    tip: { titleKey: 'commandCenter.friendly.tip.title', bodyKey: 'commandCenter.friendly.medical.tip' },
    itemLabelKey: 'commandCenter.friendly.medical.itemLabel',
    emptyKey: 'commandCenter.friendly.medical.empty',
    tourSteps: [
        { anchor: 'cc-headline-numbers', titleKey: 'commandCenter.friendly.medical.tour.s1.title', bodyKey: 'commandCenter.friendly.medical.tour.s1.body' },
        { anchor: 'cc-band-attention', titleKey: 'commandCenter.friendly.medical.tour.s2.title', bodyKey: 'commandCenter.friendly.medical.tour.s2.body' },
        { anchor: 'cc-band-medical', titleKey: 'commandCenter.friendly.medical.tour.s3.title', bodyKey: 'commandCenter.friendly.medical.tour.s3.body' },
        { anchor: 'cc-band-whatchanged', titleKey: 'commandCenter.friendly.medical.tour.s4.title', bodyKey: 'commandCenter.friendly.medical.tour.s4.body' },
    ],
};

export const FRIENDLY_CONFIG = {
    medical: MEDICAL_FRIENDLY,
    automotive: AUTOMOTIVE_FRIENDLY,
    property_management: PROPERTY_FRIENDLY,
    condo: PROPERTY_FRIENDLY,
    hotel: HOTEL_FRIENDLY,
    restaurant: RESTAURANT_FRIENDLY,
};

export function getFriendlyConfig(vertical) {
    return FRIENDLY_CONFIG[vertical] || null;
}

// Generic request-category classifier (vertical-agnostic): department ids whose
// Open-Requests rows are matched purely by `row.category === id`. Owned by the
// config module so the shared shell holds no inline department constant.
// NOTE: identical to the shell's previous inline set (byte-equal behavior).
export const CATEGORY_DEPARTMENT_IDS = [
    'maintenance', 'parking', 'waste_recycling', 'amenities', 'building',
];
